import React, { useState, useEffect, useCallback } from 'react';
import { Box, Drawer, Toolbar, Typography, IconButton, Button, Divider, Tooltip, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import CaseDetail from '../../interfaces/caseDetail';
import DropDown from '../baseComponents/dropdown';
import AddCustomerDetailsModal from './addCustomerDetailModal';
import AdditionalCaseDetailsData from '../baseComponents/additionaCaseDetailsData';
import InteractionHistoryDetails from '../baseComponents/interactionHistoryDetails';
import CurrentCaseDetails from '../baseComponents/currentCaseDetails';
import Actions from '../actions/actionsApi';
import { CustomerDetailResponse } from '../../interfaces/customerDetail';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import DispositionSelector from '../baseComponents/dispositionSelector';
import { decodedJWT, fetchCaseDetailData, fetchCustomerDetailData, getOwnerType, getUserRole } from '../../utils/commonUtils';
import RoleManagement from '../../security/roleManagement';
import apiClient from '../../axiosClient';
import JWTTokenPayload from '../../interfaces/JWTTokenPayload';
import AudioProcessingRecord from '../../interfaces/audioProcessingRecord';
import AdditionalContactReferences from '../baseComponents/additionalContactReferences';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { loadGridSelectedCase } from '../../store/slices/gridSelectedCaseSlice';
import { loadCurrentCaseID, loadCurrentClientReferenceID } from '../../store/slices/dialerCaseDataSlice';
import DialerService from '../../services/dialerService';
import { useLocation, useNavigate } from 'react-router-dom';
import Case from '../../interfaces/case';

const styles = {
	phoneContainer: {
		display: 'flex',
		gap: '5px',
		alignItems: 'center',
		marginBottom: '16px',
	},
	caseDetailBox: {
		paddingBottom: '4px'
	}
};

interface SeeDetailsDrawerProps {
	focusModeChecked?: boolean;
}

const SeeDetailsDrawer: React.FC<SeeDetailsDrawerProps> = ({ focusModeChecked = false }) => {
	const navigate = useNavigate();
	const location = useLocation();
	// parse query params
	const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
	const queryClientName = searchParams.get('client_name') || undefined;
	const queryClientReferenceId = searchParams.get('client_reference_id') || undefined;
	const queryCalllogId = searchParams.get('call_log_id') || undefined;
	// query-driven flags
	const isQueryOpen = Boolean(queryClientName && queryClientReferenceId);

	const { gridSelectedCase } = useSelector((state: RootState) => state.gridSelectedCase);
	const token: false | JWTTokenPayload = decodedJWT();
	const ownerType = getOwnerType(token);
	const userRole = getUserRole(token);
	const [open, setOpen] = useState<boolean>(false);

	const drawerWidth: string = '92vw';
	const dispatch: AppDispatch = useDispatch();
	const [caseDetailData, setCaseDetailData] = useState<CaseDetail[]>([]);
	const [addCaseDetailModalOpen, setAddCaseDetailModalOpen] = useState<boolean>(false);
	const [contactList, setContactList] = useState<string[]>([]);
	const [refreshCaseDetailToken, setRefreshCaseDetailToken] = useState<boolean>(false);
	const [refresh, setRefresh] = useState<boolean>(false);
	const [customerDetail, setCustomerDetail] = useState<CustomerDetailResponse[]>([]);
	const [prefilledDisposition, setPrefilledDisposition] = useState<Record<string, any> | null>(null);
	const [audioProcessingRecord, setAudioProcessingRecord] = useState<AudioProcessingRecord | null>(null);
	const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string>('');
	const isDialerVisible = useSelector((state: RootState) => state.dialerState.isDialerVisible);

	const load = async (cname?: string, cref?: string) => {
		if (!cname || !cref) return;
		try {
			let api_url = `/api/v1/case?client_name=${cname}&client_reference_id=${cref}`;
			if(RoleManagement.isAgent(userRole)){
				api_url = `/api/v1/agent_case_allocation/combine_allocation?client_name=${cname}&client_reference_id=${cref}`;
			}
			const resp = await apiClient.get(api_url);
			const caseData: Case = resp.data.cases[0].case;
			dispatch(loadGridSelectedCase(caseData));
		} catch (err) {
			console.error('Failed loading case from URL', err);
			alert("Failed loading case from URL");
			navigate('/');
		}
	};

	// If query params present: fetch that case only if case is not saved in redux or saved case does not match with query params
	useEffect(() => {
		if (isQueryOpen && (gridSelectedCase?.client_reference_id != queryClientReferenceId || gridSelectedCase?.client_name != queryClientName)) {
			load(queryClientName, queryClientReferenceId);
		}
	}, [queryClientName, queryClientReferenceId]);

	// When redux selection changes open/close modal
	useEffect(() => {
		setOpen(Boolean(gridSelectedCase));
	}, [gridSelectedCase]);
	
	useEffect(() => {
		if (audioProcessingRecord) {
			setPrefilledDisposition({
				"disposition_order": audioProcessingRecord.disposition_order,
				"disposition_inputs": audioProcessingRecord.disposition_inputs,
				"comment": audioProcessingRecord.summarized_comment,
				"auto_submit": false
			});
		}
	}, [audioProcessingRecord]);

	const onDispositionSuccess = async () => {
		setRefresh(!refresh);
		setPrefilledDisposition(null);
		if (RoleManagement.isAgency(ownerType)) {
			try {
				const response = await apiClient.put(`/api/v1/agent_case_allocation/update_case_score/${gridSelectedCase?.case_id}`);
				if (response.status === 200) {
					alert('case score updated successfully');
				}
			} catch (error) {
				alert('Could not update case score: ' + error);
			}
		}
	};

	const getVariant = () => {
		switch (gridSelectedCase?.status) {
			case 'Normalize':
				return { text: '#186f40', bg: '#e4fbec' };
			case 'Stabilize':
			case 'Roll Back':
				return { text: '#b68f15', bg: '#fffbef' };
			default:
				return { text: '', bg: '' };
		}
	};

	// handleClose updated
	const handleClose = useCallback(() => {
		dispatch(loadGridSelectedCase(null));
		setOpen(false);
		// clear query params but stay on same pathname (replace so history isn't noisy)
		navigate(location.pathname, { replace: true });
	}, []);

	useEffect(() => {
		// Clear AI disposition data when case changes (including switching between cases)
		setAudioProcessingRecord(null);
		setPrefilledDisposition(null);
		
		if (gridSelectedCase) {
			dispatch(loadCurrentCaseID(gridSelectedCase.case_id));
			dispatch(loadCurrentClientReferenceID(gridSelectedCase.client_reference_id));
		} else {
			dispatch(loadCurrentCaseID(''));
			dispatch(loadCurrentClientReferenceID(''));
		}
	}, [gridSelectedCase]);

	useEffect(() => {
		if (gridSelectedCase) {
			fetchCaseDetailData(gridSelectedCase, setCaseDetailData);
			fetchCustomerDetailData(gridSelectedCase, setCustomerDetail, setContactList);
		}
	}, [refreshCaseDetailToken, gridSelectedCase]);

	const customerDetailModalClose = () => {
		setAddCaseDetailModalOpen(false);
		setRefreshCaseDetailToken(!refreshCaseDetailToken);
	};

	return (
		<>
			{addCaseDetailModalOpen && gridSelectedCase && (
				<AddCustomerDetailsModal
					open={addCaseDetailModalOpen}
					onClose={customerDetailModalClose}
					selectedRow={gridSelectedCase}
				/>
			)}
			<Drawer
				sx={{
					width: drawerWidth,
					flexShrink: 0,
					'& .MuiDrawer-paper': {
						width: drawerWidth,
						boxSizing: 'border-box',
					},
				}}
				open={open} onClose={handleClose} anchor='right'>
				<Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: 'row' }}>
					<Box display="flex" gap={3}>
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<Typography variant='h6'>{gridSelectedCase?.customer_name}</Typography>
							
							<Typography variant="body1" display="flex" alignItems="center" sx={{ ml: 2 }} gap={1}>
								<span style={{ color: '#888' }}>Loan ID:</span>
								<span style={{ fontWeight: 500 }}>{gridSelectedCase?.client_reference_id}</span>
								<Tooltip title="Copy Loan ID">
									<IconButton
										size="small"
										onClick={() => navigator.clipboard.writeText(gridSelectedCase?.client_reference_id || '')}
									>
										<ContentCopyIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</Typography>

							<Box display="flex" alignItems="center" sx={{ ml: 1 }}>
								<Typography variant="body1" style={{ color: '#888' }}>Status:</Typography>
								<Chip
									sx={{ ml: 1, mr: 2, bgcolor: getVariant().bg, color: getVariant().text }}
									label={gridSelectedCase?.status}
								/>
							</Box>

							{!RoleManagement.isAgent(getUserRole(token)) && RoleManagement.isAgency(getOwnerType(token)) && (
								<Box display="flex" alignItems="center">
									<Typography variant="body1" style={{ color: '#888' }}>Allocated Agents:</Typography>
									<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 1 }}>
										{(gridSelectedCase?.assigned_agents ?? []).map((agent, index) => (
											<Chip key={index} label={agent} />
										))}
									</Box>
								</Box>
							)}
						</Box>
					</Box>
					<IconButton onClick={handleClose} aria-label="close drawer" >
						<CloseIcon />
					</IconButton>
				</Toolbar>
				<Divider />
				<div style={{ display: 'flex' }}> 
					{/* column 1 */}
					<div style={{ width: '30vw' }}>
						<Box sx={{ padding: 2, overflowY: 'auto' }}>
							<Box sx={styles.phoneContainer}>
								<IconButton disabled={!isDialerVisible || !gridSelectedCase} onClick={() => DialerService.scheduleCallBack(gridSelectedCase?.case_id || '', selectedPhoneNumber)}>
									<PhoneIcon />
								</IconButton>
								<Box sx={{ width: '13vw' }}>
									<DropDown
										label="Select Phone Number"
										selectedValue={selectedPhoneNumber}
										onSelection={val => setSelectedPhoneNumber(val)}
										items={contactList}
									/>
								</Box>
								<Button variant="outlined" onClick={() => setAddCaseDetailModalOpen(true)}>
									Add More Details
								</Button>
							</Box>
							<AdditionalContactReferences
								client_reference_id={gridSelectedCase?.client_reference_id || ''}
								customerDetailData={customerDetail}
							/>
							<Divider />
							{gridSelectedCase && (
								<div style={styles.caseDetailBox}>
									<CurrentCaseDetails refresh={refreshCaseDetailToken} case_id={gridSelectedCase.case_id} caseDetailData ={caseDetailData}/>
								</div>
							)}
							<Divider />
							{gridSelectedCase && (
								<Actions
									setRefresh={setRefreshCaseDetailToken}
									case_id={gridSelectedCase.case_id}
									client_name={gridSelectedCase.client_name}
									refresh={refreshCaseDetailToken}
								/>
							)}
							<AdditionalCaseDetailsData
								caseDetailData={caseDetailData}
								customerDetailData={customerDetail}
							/>
							<Divider sx={{ marginY: "16px" }} />
						</Box>

					</div>
					{gridSelectedCase && (
						<Box>
							<Box sx={{ display: 'flex' }}>
								{/* column 2 */}
								<div style={{ width: '28vw' }}>
									<DispositionSelector
										onDispositionSubmitSuccess={onDispositionSuccess}
										selectedRow={gridSelectedCase}
										prefilledDisposition={prefilledDisposition}
										setAudioProcessingRecord={setAudioProcessingRecord}
										callLogId={queryCalllogId}
									/>
								</div >
								{/* column 3 */}
								<div style={{ width: '32vw', paddingLeft:'8px' }} >
									<InteractionHistoryDetails
										refresh={refresh}
										client_name={gridSelectedCase.client_name}
										client_reference_id={gridSelectedCase.client_reference_id}
										case_id={gridSelectedCase.case_id}
									/>
								</div>
							</Box>
						</Box>
					)}
				</div >
			</Drawer >
		</>
	);
};

export default SeeDetailsDrawer;
